import { httpsCallable, functions } from '@/lib/functions';
import { logger } from '@/services/logger';
import { markTripsForListRecovery, refreshTripListFromServer } from '@/services/trips';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

const acceptPendingInvitationsWithEntitlementCallable = httpsCallable<
  { userName?: string },
  { success: boolean; acceptedTripIds: string[] }
>(functions, 'acceptPendingInvitationsWithEntitlement');

const acceptPendingInFlightByUser = new Map<string, Promise<string[]>>();

/**
 * Applies pending collaborator invitations addressed to the authenticated account email.
 * Returns accepted trip IDs. Concurrent calls for the same user share one in-flight request.
 */
export async function checkAndAcceptPendingInvitations(
  userId: string,
  userEmail: string,
): Promise<string[]> {
  const existing = acceptPendingInFlightByUser.get(userId);
  if (existing) {
    return existing;
  }

  const promise = (async (): Promise<string[]> => {
    try {
      const { data } = await acceptPendingInvitationsWithEntitlementCallable({});
      const acceptedTripIds = Array.isArray(data?.acceptedTripIds) ? data.acceptedTripIds : [];
      if (acceptedTripIds.length > 0) {
        logger.info(`Accepted ${acceptedTripIds.length} pending invitation(s)`, {
          userId,
          userEmail,
          tripIds: acceptedTripIds,
        });
        markTripsForListRecovery(userId, acceptedTripIds);
        acceptedTripIds.forEach((tripId) => {
          void logAnalyticsEvent('invite_accepted', {
            tripId,
            actorId: userId,
            source: 'pending_invitation',
            result: 'success',
          });
          void logAnalyticsEvent('collaborator_joined', {
            tripId,
            collaboratorId: userId,
            source: 'pending_invitation',
          });
        });
        try {
          await refreshTripListFromServer(userId);
        } catch (refreshError) {
          logger.warn('Accepted invites but failed to refresh trip list', { userId }, refreshError as Error);
        }
      }
      return acceptedTripIds;
    } catch (error) {
      logger.error('Error checking pending invitations', error);
      return [];
    } finally {
      acceptPendingInFlightByUser.delete(userId);
    }
  })();

  acceptPendingInFlightByUser.set(userId, promise);
  return promise;
}
